import { useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { api } from '../../api/client'
import { PageHeader } from '../../components/PageHeader'
import { Modal } from '../../components/Modal'
import { useI18n } from '../../i18n'

type Invitation = { id: string; code: string; note: string; usedBy: string; usedAt: string | null; createdAt: string }

export function InvitationsPage() {
  const { t } = useI18n()
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [error, setError] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [note, setNote] = useState('')

  useEffect(() => {
    api.get<Invitation[]>('/admin/invitations').then(setInvitations).catch((e: Error) => setError(e.message))
  }, [])

  async function create() {
    try {
      const item = await api.post<Invitation>('/admin/invitations', { note })
      setInvitations((current) => [item, ...current])
      setShowCreate(false)
      setNote('')
    } catch (e) { setError(e instanceof Error ? e.message : 'Error') }
  }

  async function remove(id: string) {
    try {
      await api.delete(`/admin/invitations/${id}`)
      setInvitations((current) => current.filter((i) => i.id !== id))
    } catch (e) { setError(e instanceof Error ? e.message : 'Error') }
  }

  return <>
    <PageHeader title="admin.invitations" description="admin.invitationsDescription" action={
      <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2"><Plus size={16} />{t('admin.createInvitation')}</button>
    } />
    {error && <p className="mb-5 rounded-lg bg-destructive/10 px-4 py-2.5 text-sm text-destructive">{error}</p>}

    {/* Mobile card view */}
    <div className="space-y-3 sm:hidden">
      {invitations.length === 0 && <p className="text-center py-8 text-sm text-muted-foreground">{t('common.noResults')}</p>}
      {invitations.map((inv) => (
        <div key={inv.id} className="panel p-4">
          <div className="flex items-start justify-between">
            <code className="rounded-md bg-muted px-2 py-1 font-mono text-xs">{inv.code}</code>
            <span className={`badge ${inv.usedAt ? 'bg-muted text-muted-foreground' : 'bg-green-100 text-green-700'}`}>
              {inv.usedAt ? t('admin.invitationUsed') : t('admin.invitationUnused')}
            </span>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>{inv.note || '—'}</span>
            <span>{new Date(inv.createdAt).toLocaleDateString()}</span>
          </div>
          {!inv.usedAt && (
            <div className="mt-3 border-t pt-3">
              <button onClick={() => remove(inv.id)} className="text-xs text-destructive hover:underline">{t('common.delete')}</button>
            </div>
          )}
        </div>
      ))}
    </div>

    {/* Desktop table view */}
    <div className="hidden sm:block panel overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left font-medium">{t('admin.invitationCode')}</th>
              <th className="px-4 py-3 text-left font-medium">{t('admin.invitationNote')}</th>
              <th className="px-4 py-3 text-left font-medium">{t('common.status')}</th>
              <th className="px-4 py-3 text-left font-medium">{t('admin.time')}</th>
              <th className="px-4 py-3 text-right font-medium">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {invitations.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">{t('common.noResults')}</td></tr>}
            {invitations.map((inv) => (
              <tr key={inv.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3"><code className="rounded-md bg-muted px-2 py-0.5 font-mono text-xs">{inv.code}</code></td>
                <td className="px-4 py-3 text-muted-foreground">{inv.note || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`badge ${inv.usedAt ? 'bg-muted text-muted-foreground' : 'bg-green-100 text-green-700'}`}>
                    {inv.usedAt ? t('admin.invitationUsed') : t('admin.invitationUnused')}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{new Date(inv.createdAt).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right">
                  {!inv.usedAt && <button onClick={() => remove(inv.id)} className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10 transition-colors"><Trash2 size={15} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    <Modal open={showCreate} onClose={() => { setShowCreate(false); setNote('') }} title={t('admin.createInvitation')}>
      <div className="space-y-4">
        <input className="field" placeholder={t('admin.invitationNote') + '（' + t('common.optional') + '）'} value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
        <div className="flex gap-3">
          <button onClick={create} className="btn-primary">{t('admin.createInvitation')}</button>
          <button onClick={() => { setShowCreate(false); setNote('') }} className="btn-secondary">{t('common.cancel')}</button>
        </div>
      </div>
    </Modal>
  </>
}
